import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Download, ArrowRight } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // ~60s

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [clipId, setClipId] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        const st = r.data.payment_status;
        if (r.data.clip_id) setClipId(r.data.clip_id);
        if (st === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired", "refunded"].includes(st)) {
          setState("failed");
          return;
        }
      } catch (_) {
        // transient — keep polling until timeout
      }
      if (pollsRef.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-stone-950 flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {state === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto text-orange-500 animate-spin mb-4" />
            <h1 className="font-display uppercase text-3xl font-bold">Confirming your purchase…</h1>
            <p className="text-stone-400 mt-2 text-sm">
              Hang tight — Stripe is telling us the transaction cleared. Usually 2-3 seconds.
            </p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-500 mb-4" />
            <h1 className="font-display uppercase text-3xl font-bold">Purchase complete</h1>
            <p className="text-stone-400 mt-2 text-sm">
              Your clean, watermark-free clip is ready. You can also re-download any time from
              My&nbsp;Clips.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
              {clipId && (
                <a
                  href={`${BACKEND_URL}/api/clips/${clipId}/download`}
                  className="btn-primary inline-flex items-center gap-2 text-sm"
                  data-testid="download-purchased-clip"
                >
                  <Download size={14} /> Download now
                </a>
              )}
              <button
                onClick={() => navigate("/portal/my-clips")}
                className="btn-secondary inline-flex items-center gap-2 text-sm"
                data-testid="back-to-my-clips"
              >
                Back to My Clips <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={56} className="mx-auto text-rose-500 mb-4" />
            <h1 className="font-display uppercase text-3xl font-bold">Payment failed</h1>
            <p className="text-stone-400 mt-2 text-sm">
              Something went wrong. Your card was not charged. Try again from My Clips.
            </p>
            <button
              onClick={() => navigate("/portal/my-clips")}
              className="btn-primary mt-6 inline-flex items-center gap-2 text-sm"
            >
              Back to My Clips
            </button>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 size={48} className="mx-auto text-stone-500 mb-4" />
            <h1 className="font-display uppercase text-3xl font-bold">Still processing</h1>
            <p className="text-stone-400 mt-2 text-sm">
              This is taking longer than expected. If your card was charged, refresh My Clips in a
              minute — the download will appear there.
            </p>
            <button
              onClick={() => navigate("/portal/my-clips")}
              className="btn-primary mt-6 inline-flex items-center gap-2 text-sm"
            >
              Go to My Clips
            </button>
          </>
        )}
      </div>
    </div>
  );
}
